import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { connect } from 'react-redux';
import { change, Field, FieldArray, reduxForm } from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Typography } from '@material-ui/core';

import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import HelpIcon from '@material-ui/icons/Help';

import {
  CircularLoader,
  CustomDateRangePicker,
  DropDown,
  PopoverWrapper
} from 'components';

import { getParticipants } from 'utils/book-accommodation/prepareParticipants';
import { getReceivedAccommodationData } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import {
  getAvailableRangeDates,
  getDisabledDates
} from 'utils/book-accommodation/availableDates';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import NumberOfParticipants from 'pages/book-activity/components/number-of-participants/NumberOfParticipants';
import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';

import { ACCOMMODATION, BOOK_DATES } from 'graphql/queries/accommodations';

import validate from './validate';

import { useStyles } from '../styles';

const initialValues = {
  participants: [],
  addOns: [],
  mandatoryFees: []
};

const FIXED_PRICES = 'pricesFixedForm';
const CATEGORY_PRICES = 'pricesCategoryForm';
const DAILY_PRICES = 'pricesDailyForm';
const FUTURE_PRICES = 'futurePricesForm';

const BookAccommodationDialog = ({
  open,
  ownerId,
  onCancel,
  onConfirm,
  onUpdate,
  relatedAccommodationsForm,
  currency,
  bookRelatedAccommodationForm,
  changeFormFieldValue,
  handleSubmit,
  initialValues
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const initialDates = initialValues.dates;
  const startDate = initialDates ? initialDates.startDate : moment();

  const [isInitial, setIsInitial] = React.useState(!!initialValues.title);
  const [accommodationId, setAccommodationId] = React.useState(null);
  const [accommodation, setAccommodation] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );

  const { loading, data } = useQuery(ACCOMMODATION, {
    skip: !accommodationId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId,
      businessOwnerId: ownerId
    }
  });

  const {
    loading: bookDatesLoading,
    data: bookDatesData,
    refetch: bookDatesRefetch
  } = useQuery(BOOK_DATES, {
    skip: !accommodationId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId,
      businessOwnerId: ownerId,
      startPeriod: moment(startDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(startDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (data && data.accommodations.length) {
      const receivedAccommodation = data.accommodations[0];
      const accommodationData = getReceivedAccommodationData(
        receivedAccommodation
      );
      if (receivedAccommodation.futurePrices?.[0]?.ratePlanPrices) {
        accommodationData['pricesType'] = FUTURE_PRICES;
      } else if (receivedAccommodation.prices[0].participant.participant) {
        accommodationData['pricesType'] = CATEGORY_PRICES;
      } else if (receivedAccommodation.prices[0].day) {
        accommodationData['pricesType'] = DAILY_PRICES;
      } else {
        accommodationData['pricesType'] = FIXED_PRICES;
      }
        
      setParticipants(accommodationData);
      setAddOns(accommodationData);
      setMandatoryFees(accommodationData);
      setMaxParticipants(accommodationData);

      changeFormFieldValue(
        'minNights',
        accommodationData.generalForm.minNights
      );

      if (!isInitial) {
        changeFormFieldValue('title', accommodationData.generalForm.title);
        changeFormFieldValue('pricesType', accommodationData['pricesType']);
        changeFormFieldValue('dates', null);
      }

      setIsInitial(false);
      setAccommodation(accommodationData);
    }
  }, [data]);

  React.useEffect(() => {
    const selectedAccommodationId =
      bookRelatedAccommodationForm.values.accommodation;

    if (selectedAccommodationId) {
      setAccommodationId(selectedAccommodationId);
    }
  }, [bookRelatedAccommodationForm.values.accommodation]);

  React.useEffect(() => {
    if (!bookRelatedAccommodationForm || !accommodation) return;
    setParticipants(accommodation);
  }, [bookRelatedAccommodationForm.values.dates]);

  const changeMonthHandler = date => {
    bookDatesRefetch({
      accommodationId: accommodationId,
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const setParticipants = accommodation => {
    setIsUpdatedParticipants(!isUpdatedParticipants);

    const previousParticipants =
      bookRelatedAccommodationForm.values.participants || [];
    const dates = bookRelatedAccommodationForm.values.dates;

    if (dates && dates.startDate && dates.endDate) {
      const participants = getParticipants(
        accommodation,
        currency,
        previousParticipants,
        dates
      );
      changeFormFieldValue('participants', participants);
    } else {
      changeFormFieldValue('participants', []);
    }
  };

  const setAddOns = accommodation => {
    const previousAddOns = bookRelatedAccommodationForm.values.addOns || [];

    const addOnsList = accommodation.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const setMandatoryFees = accommodation => {
    const previousMandatoryFees = bookRelatedAccommodationForm.values.mandatoryFees || [];
    const mandatoryFeesList = accommodation.mandatoryFeesForm.mandatoryFees.map(item => {
      const theSameMandatoryFee = previousMandatoryFees.find(mandatoryFee => mandatoryFee.id === item.id);
      return {
        ...item,
        value: theSameMandatoryFee ? theSameMandatoryFee.value : 0,
        originalId: theSameMandatoryFee ? theSameMandatoryFee.originalId : null
      };
    });
    changeFormFieldValue('mandatoryFees', mandatoryFeesList);
  };

  const setMaxParticipants = accommodation => {
    changeFormFieldValue('maxParticipants', accommodation.generalForm.guests);
  };

  const confirmWrapper = item => {
    const generalFormNorwegian = accommodation['generalForm-Norwegian'];
    const norwegianFields = {};

    if (generalFormNorwegian) {
      norwegianFields['title'] = generalFormNorwegian.title;
    }
    onConfirm({
      ...item,
      Norwegian: norwegianFields
    });
  };

  return (
    <Dialog open={open} scroll="paper" classes={{ paper: classes.paper }}>
      <CircularLoader
        isLoading={loading || bookDatesLoading}
        loaderClassName={classes.loaderClassName}
      />

      <IconButton className={classes.closeBtn} onClick={onCancel}>
        <HighlightOffIcon className={classes.closeIcon} />
      </IconButton>

      <DialogTitle
        id="alert-dialog-title"
        classes={{ root: classes.title }}
        disableTypography={true}
      >
        {accommodation
          ? getFormByCurrentLanguage(
              selectedLanguage,
              accommodation.generalForm,
              accommodation['generalForm-Norwegian']
            ).title
          : t('Book Accommodation')}
        <PopoverWrapper text="Note you can book only an accommodation that is related to its activity">
          <HelpIcon className={classes.helpIcon} />
        </PopoverWrapper>
      </DialogTitle>

      <form
        onSubmit={handleSubmit(item =>
          !!initialValues.title ? onUpdate(item) : confirmWrapper(item)
        )}
      >
        <DialogContent classes={{ root: classes.contentRoot }}>
          {accommodation && (
            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.location}
                </Typography>
              </div>
              <div className={classes.infoItem}>
                <PeopleOutlineIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.guests} {t('Guests')}
                </Typography>
              </div>
            </div>
          )}
          <Field
            name="accommodation"
            className={classes.inputIndent}
            list={relatedAccommodationsForm.relatedAccommodations}
            field="title"
            label="Accommodation *"
            component={DropDown}
          />
          {accommodation && (
            <>
              <Field
                name="dates"
                availableRange={getAvailableRangeDates(accommodation)}
                disabledDates={getDisabledDates(bookDatesData, initialDates)}
                changeMonthsCallback={changeMonthHandler}
                className={classes.inputIndent}
                label="Select dates *"
                daySize={39}
                component={CustomDateRangePicker}
                isStartDateRenderMonth={!!initialDates}
              />
              {!!bookRelatedAccommodationForm.values.participants.length && (
                <>
                  <Typography variant="body1" className={classes.sectionTitle}>
                    {t('Guests')} *
                  </Typography>
                  <FieldArray
                    name="participants"
                    className={classes.participants}
                    errorClassName={classes.participantsErrorMsg}
                    isUpdatedParticipants={isUpdatedParticipants}
                    component={NumberOfParticipants}
                    currency={currency}
                  />
                </>
              )}

              {!!bookRelatedAccommodationForm.values.mandatoryFees.length }
              
              {!!bookRelatedAccommodationForm.values.addOns.length && (
                <>
                  <Typography variant="body1" className={classes.sectionTitle}>
                    {t('Add-ons')}
                  </Typography>
                  <FieldArray
                    name="addOns"
                    currency={currency}
                    component={AddOnsList}
                    addOnDescClass={classes.addOnTitle}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions classes={{ root: classes.actionsRoot }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            classes={{ root: classes.cancelBtnRoot }}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            classes={{ root: classes.actionBtnRoot }}
            autoFocus
          >
            {t('Book')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {
    bookRelatedAccommodationForm: state.form.bookRelatedAccommodationForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookRelatedAccommodationForm', field, value));
  }
});

export default reduxForm({
  form: 'bookRelatedAccommodationForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookAccommodationDialog));
